import React, { useState, useRef, useEffect } from 'react';
import { getLocationAutocomplete } from '../services/api';

interface SearchFormProps {
  onSearch: (query: string, location: string, filters: any) => void;
  isLoading: boolean;
  isSearchBlocked?: boolean;
  initialQuery?: string;
  initialLocation?: string;
  initialFilters?: any;
  /** For free tier: restrict location autocomplete to this country (ISO code) */
  searchCountry?: string | null;
  /** For free tier: show hint about search area restriction */
  isFreeTier?: boolean;
  /** Display name of user's search country for hint */
  searchCountryName?: string | null;
  /** User's subscription tier (for blocked overlay messaging) */
  searchTier?: string;
  /** Number of results from last search (shown when available) */
  resultCount?: number;
  /** Max results per search from plan */
  maxResultsPerSearch?: number;
}

const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States', CA: 'Canada', GB: 'United Kingdom', AU: 'Australia',
  DE: 'Germany', FR: 'France', ES: 'Spain', IT: 'Italy', NL: 'Netherlands',
  BE: 'Belgium', IE: 'Ireland', NZ: 'New Zealand', IN: 'India', SG: 'Singapore',
  AE: 'United Arab Emirates', MX: 'Mexico', BR: 'Brazil', JP: 'Japan', KR: 'South Korea',
};

const SearchForm: React.FC<SearchFormProps> = ({ 
  onSearch, 
  isLoading, 
  isSearchBlocked = false,
  initialQuery = '',
  initialLocation = '',
  initialFilters = {},
  searchCountry,
  isFreeTier = false,
  searchCountryName,
  searchTier,
  resultCount,
  maxResultsPerSearch,
}) => {
  const disabled = isLoading || isSearchBlocked;
  const [query, setQuery] = useState(initialQuery);
  const [location, setLocation] = useState(initialLocation);
  const [noWebsiteOnly, setNoWebsiteOnly] = useState(initialFilters.no_website_only || false);
  const [socialMediaFilter, setSocialMediaFilter] = useState<string>(
    initialFilters.has_social_media === true ? 'with' : 
    initialFilters.has_social_media === false ? 'without' : 'all'
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [minRating, setMinRating] = useState<number | ''>(initialFilters.minRating || '');
  const [maxRating, setMaxRating] = useState<number | ''>(initialFilters.maxRating || '');
  const [minReviewCount, setMinReviewCount] = useState<number | ''>(initialFilters.minReviewCount || '');
  const [maxReviewCount, setMaxReviewCount] = useState<number | ''>(initialFilters.maxReviewCount || '');
  const [minOpportunityScore, setMinOpportunityScore] = useState<number | ''>(initialFilters.minOpportunityScore || '');
  const [maxOpportunityScore, setMaxOpportunityScore] = useState<number | ''>(initialFilters.maxOpportunityScore || '');
  const [maxDistance, setMaxDistance] = useState<number | ''>(initialFilters.maxDistance || '');
  const [suggestions, setSuggestions] = useState<Array<{ description: string; place_id?: string }>>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextFetchRef = useRef(false);
  const locationWrapperRef = useRef<HTMLDivElement>(null);

  // Debounced location autocomplete
  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    if (location.length < 2) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      getLocationAutocomplete(location, searchCountry)
        .then((res) => setSuggestions(res.predictions || []))
        .catch(() => setSuggestions([]));
      debounceRef.current = null;
    }, 280);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [location, searchCountry]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (locationWrapperRef.current && !locationWrapperRef.current.contains(e.target as Node)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleSelectLocation = (description: string) => {
    setLocation(description);
    setSuggestions([]);
    skipNextFetchRef.current = true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const filters: any = {
      no_website_only: noWebsiteOnly,
      has_social_media: socialMediaFilter === 'all' ? undefined : socialMediaFilter === 'with' ? true : false,
    };
    
    // Add advanced filters if set
    if (minRating !== '') filters.minRating = Number(minRating);
    if (maxRating !== '') filters.maxRating = Number(maxRating);
    if (minReviewCount !== '') filters.minReviewCount = Number(minReviewCount);
    if (maxReviewCount !== '') filters.maxReviewCount = Number(maxReviewCount);
    if (minOpportunityScore !== '') filters.minOpportunityScore = Number(minOpportunityScore);
    if (maxOpportunityScore !== '') filters.maxOpportunityScore = Number(maxOpportunityScore);
    if (maxDistance !== '') filters.maxDistance = Number(maxDistance);
    
    onSearch(query, location, filters);
  };

  const handleClearFilters = () => {
    setNoWebsiteOnly(false);
    setSocialMediaFilter('all');
    setMinRating('');
    setMaxRating('');
    setMinReviewCount('');
    setMaxReviewCount('');
    setMinOpportunityScore('');
    setMaxOpportunityScore('');
    setMaxDistance('');
  };

  const hasActiveFilters = noWebsiteOnly || socialMediaFilter !== 'all' || 
    minRating !== '' || maxRating !== '' || minReviewCount !== '' || maxReviewCount !== '' ||
    minOpportunityScore !== '' || maxOpportunityScore !== '' || maxDistance !== '';

  return (
    <div className="w-full bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-8 relative">
      {isSearchBlocked && (
        <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center z-10">
          <p className="text-slate-700 font-semibold mb-2">Out of search credits</p>
          <p className="text-sm text-slate-500 mb-4">
            {searchTier === 'free'
              ? 'Wait for daily reset or upgrade for more searches.'
              : searchTier === 'basic'
              ? 'Wait for monthly reset or upgrade for unlimited searches.'
              : 'Upgrade for unlimited searches.'}
          </p>
          <a href="/pricing" className="px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors">
            Upgrade now
          </a>
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          {/* Query Input - same structure as Location for alignment */}
          <div className="flex-1 w-full lg:w-auto flex flex-col">
            <label className="block text-sm font-bold text-slate-900 mb-2 ml-1">Business Category</label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g., plumber, restaurant, dentist"
              className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium text-slate-700 placeholder:text-slate-400 hover:border-slate-300"
              disabled={disabled}
              required
            />
          </div>

          {/* Location Input with Autocomplete - same structure for alignment */}
          <div className="flex-1 w-full lg:w-auto flex flex-col" ref={locationWrapperRef}>
            <label className="block text-sm font-bold text-slate-900 mb-2 ml-1">Location</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors z-10">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, Zip, or 'Current Location'"
                className="w-full pl-11 pr-4 py-2.5 bg-white border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium text-slate-700 placeholder:text-slate-400 hover:border-slate-300"
                disabled={disabled}
                required
                autoComplete="off"
              />
              {suggestions.length > 0 && (
                <ul className="absolute left-0 right-0 top-full mt-1 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                  {suggestions.map((s, i) => (
                    <li key={s.place_id || `loc-${i}`}>
                      <button
                        type="button"
                        onClick={() => handleSelectLocation(s.description)}
                        className="w-full text-left px-4 py-2.5 pl-11 text-sm font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                      >
                        {s.description}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Search Button */}
          <div className="w-full lg:w-auto shrink-0">
          <button
            type="submit"
            disabled={disabled}
            className="w-full lg:w-auto flex items-center justify-center gap-2 px-8 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold transition-all shadow-sm active:scale-95 disabled:bg-slate-300 whitespace-nowrap"
          >
            {isLoading ? (
              <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
            Search Leads
          </button>
          </div>
        </div>

        {/* Country & results info - shared row */}
        <p className="text-xs text-slate-500 ml-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          {resultCount !== undefined && resultCount >= 0 && (
            <>
              <span className="font-medium text-indigo-600">{resultCount} results</span>
              <span className="text-slate-400">·</span>
            </>
          )}
          {isFreeTier && (searchCountryName || searchCountry) ? (
            <span>
              Your plan: search within <span className="font-medium text-indigo-600">{searchCountryName || COUNTRY_NAMES[searchCountry || ''] || searchCountry}</span> only
            </span>
          ) : (
            <span>Your plan: <span className="font-medium text-indigo-600">worldwide</span></span>
          )}
          {maxResultsPerSearch != null && maxResultsPerSearch > 0 && (
            <>
              <span className="text-slate-400">·</span>
              <span>Up to {maxResultsPerSearch} results per search</span>
            </>
          )}
        </p>
      </form>

      {/* Basic Filters */}
      <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap gap-4 items-center">
        <label className="custom-checkbox flex items-center gap-2 cursor-pointer group">
          <input
            type="checkbox"
            checked={noWebsiteOnly}
            onChange={(e) => setNoWebsiteOnly(e.target.checked)}
          />
          <span className="checkbox-label"></span>
          <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors ml-1">
            Show only businesses without websites
          </span>
        </label>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-700">Social Media:</label>
          <div className="custom-select">
            <select
              value={socialMediaFilter}
              onChange={(e) => setSocialMediaFilter(e.target.value)}
            >
              <option value="all">All Businesses</option>
              <option value="with">With Social Media</option>
              <option value="without">Without Social Media</option>
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="ml-auto px-4 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
        >
          {showAdvanced ? '▼' : '▶'} Advanced Filters
        </button>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Rating Range */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Rating Range</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={minRating}
                onChange={(e) => setMinRating(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Min"
                className="custom-input"
              />
              <span className="text-slate-400 font-medium">-</span>
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={maxRating}
                onChange={(e) => setMaxRating(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Max"
                className="custom-input"
              />
            </div>
          </div>

          {/* Review Count Range */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Review Count Range</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                value={minReviewCount}
                onChange={(e) => setMinReviewCount(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Min"
                className="custom-input"
              />
              <span className="text-slate-400 font-medium">-</span>
              <input
                type="number"
                min="0"
                value={maxReviewCount}
                onChange={(e) => setMaxReviewCount(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Max"
                className="custom-input"
              />
            </div>
          </div>

          {/* Opportunity Score Range */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Opportunity Score Range</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="100"
                value={minOpportunityScore}
                onChange={(e) => setMinOpportunityScore(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Min"
                className="custom-input"
              />
              <span className="text-slate-400 font-medium">-</span>
              <input
                type="number"
                min="0"
                max="100"
                value={maxOpportunityScore}
                onChange={(e) => setMaxOpportunityScore(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Max"
                className="custom-input"
              />
            </div>
          </div>

          {/* Max Distance */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Max Distance (miles)</label>
            <input
              type="number"
              min="0"
              value={maxDistance}
              onChange={(e) => setMaxDistance(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="e.g., 10"
              className="custom-input"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchForm;
